import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { sageApi, usersApi } from '@/lib/apiClient';
import { Button } from '@/components';

/**
 * Sage Intacct sync status + control, shared across surfaces.
 *
 * The manual "Sync now · all jobs" button lives in ONE global place —
 * Admin › Integrations (variant="full") — instead of being scattered per-tab
 * and per-job. The data tabs that consume sync data (Materials, Change Mgmt)
 * render a read-only status line (variant="status") so PMs can see how fresh
 * the Intacct data is without being able to kick off a company-wide pull.
 *
 * Read-only FROM Intacct: the sync fills our rows; nothing here ever writes
 * back. A nightly @Cron on the API runs the same pull automatically — a run
 * with no runBy is a scheduled one (surfaced as "· scheduled").
 */
export function SageSyncPanel({ variant, rightSlot, showMatching = true }: {
  variant: 'full' | 'status';
  /** Extra control rendered on the right (e.g. Materials' "+ Add Material"). */
  rightSlot?: ReactNode;
  /**
   * Show the PO-line → Materials matching detail (matched count + unmatched
   * review list). True for Materials/Admin; FALSE on Change Mgmt, where PO
   * matching is irrelevant and only the sync freshness matters.
   */
  showMatching?: boolean;
}) {
  const qc = useQueryClient();
  const [showReview, setShowReview] = useState(false);

  // Shared status query (one cache key app-wide) — a sync run in Admin updates
  // the read-only lines on Materials/Change Mgmt without a refetch.
  const { data: s } = useQuery({
    queryKey: ['sage-status'],
    queryFn: () => sageApi.status(),
    staleTime: 10_000,
    refetchInterval: (q) => (q.state.data?.running ? 4_000 : false),
  });

  // Only the full (Admin) variant can trigger a run, and only for holders of
  // materials.edit (Admin short-circuits) — mirrors the backend guard.
  const { data: me } = useQuery({
    queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000,
    enabled: variant === 'full',
  });
  const canSync = variant === 'full'
    && ((me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('materials.edit') ?? false));

  const syncMut = useMutation({
    mutationFn: () => sageApi.sync(),
    onSuccess: () => {
      // The trigger now returns { started } and runs in the background — refetch
      // the real status (it'll show running=true, which drives the poll) rather
      // than caching the trigger response.
      qc.invalidateQueries({ queryKey: ['sage-status'] });
      // Refresh everything the sync touches so the data tabs reflect it.
      qc.invalidateQueries({ queryKey: ['materials'] });
      qc.invalidateQueries({ queryKey: ['sage-lines'] });
      qc.invalidateQueries({ queryKey: ['project-counts'] });
      qc.invalidateQueries({ queryKey: ['changes'] });
      qc.invalidateQueries({ queryKey: ['sage-contract'] });
    },
    // Timeout / dropped connection: the server may still be running the sync —
    // re-poll rather than trust the failed request.
    onError: () => qc.invalidateQueries({ queryKey: ['sage-status'] }),
  });

  const last = s?.lastRun ?? null;
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  // PO-line matching detail is only meaningful where materials live.
  const matchClause = showMatching && s ? ` · ${s.totals.matched} lines matched · ${last?.unmatched ?? 0} need review` : '';
  const summary = !s ? 'Checking sync status…'
    : !s.configured ? 'Sage credentials not configured on this environment — manual entry until then'
    : !last ? (showMatching
        ? 'Never synced — run the first sync to pull PO numbers, committed cost, contracts and change orders from Intacct'
        : 'Not synced from Intacct yet')
    : last.status === 'error' ? `Last sync failed: ${last.error ?? 'unknown error'}`
    : `Last synced ${fmt(last.finishedAt ?? last.startedAt)} ${last.runBy ? `by ${last.runBy}` : '· scheduled'}${matchClause}`;

  const running = !!s?.running;

  return (
    <div style={{ background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '10px var(--ew-s-4)', marginBottom: 'var(--ew-s-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', flexWrap: 'wrap' }}>
        <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: s?.configured ? 'var(--ew-quality)' : 'var(--ew-n-500)' }}>
          ◇ {s?.configured ? 'Synced from Sage Intacct' : 'Manual tracking'}
        </span>
        <span style={{ font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: last?.status === 'error' ? 'var(--ew-danger)' : 'var(--ew-n-500)' }}>{summary}</span>
        {showMatching && s && s.review.length > 0 ? (
          <button className="ew-btn-link" style={{ color: 'var(--ew-safety-700)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }} onClick={() => setShowReview((v) => !v)}>
            {showReview ? 'Hide review list' : `Review ${s.review.length} unmatched`}
          </button>
        ) : null}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {canSync && s?.configured ? (
            <span title="Pulls PO numbers, committed cost, contract values & change orders from Sage Intacct for ALL jobs (read-only — nothing is written to Intacct). After the first pull, each run fetches only what changed.">
              <Button variant="secondary" size="sm" onClick={() => syncMut.mutate()} isLoading={syncMut.isPending || running}>
                {syncMut.isPending || running ? 'Syncing…' : 'Sync now · all jobs'}
              </Button>
            </span>
          ) : null}
          {rightSlot ?? null}
        </div>
      </div>

      {/* Full (Admin) variant: state the nightly-schedule truth (it ships dark
          behind SAGE_PO_SYNC_ENABLED, so don't claim it's on when it isn't). */}
      {variant === 'full' && s?.configured ? (
        <div style={{ marginTop: 6, font: 'var(--ew-fw-regular) 11.5px/1.4 var(--ew-font)', color: 'var(--ew-n-400)' }}>
          {s.schedule?.enabled
            ? 'Runs automatically every night (~3 AM Eastern). Use "Sync now" only when you need the latest Intacct data on demand.'
            : 'Nightly automatic sync is off — set SAGE_PO_SYNC_ENABLED=true on the API to turn it on. Until then, use "Sync now".'}
        </div>
      ) : null}

      {syncMut.isError ? (
        <div style={{ marginTop: 8, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>
          {(syncMut.error as Error).message}
        </div>
      ) : null}

      {showReview && s ? (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--ew-n-100)', paddingTop: 8 }}>
          <div style={{ font: 'var(--ew-fw-regular) 11px/1.4 var(--ew-font)', color: 'var(--ew-n-500)', marginBottom: 6 }}>
            PO lines on known jobs with no single matching material line (missing row, or several rows share the cost code).
            Add or fix the material line, then re-sync.
          </div>
          {s.review.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--ew-n-100)', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, minWidth: 70 }}>{r.jobNumber}</span>
              <span style={{ color: 'var(--ew-n-500)', minWidth: 80 }}>{r.costCode ?? '—'}</span>
              <span style={{ flex: 1, minWidth: 140 }}>{r.item ?? '—'}</span>
              <span style={{ color: 'var(--ew-n-500)' }}>{r.docNo}</span>
              <span style={{ fontWeight: 600 }}>{r.value != null ? `$${r.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
